import { useEffect as useEffectTS, useRef as useRefTS, useState as useStateTS } from "react";
import { Api } from "./api";
import { Banner, Icon } from "./components";

// Town Square — per-community discussion board.
// Topics organized by category, flat reply chains, 👍 agreement only,
// manager-only pin / lock / resolved flags. No delete in MVP.

function _relTimeTS(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function _canModerate(user) {
  const role = user?.role;
  return role === "manager" || role === "company_admin" || role === "super_admin";
}

function TownSquare({ user }) {
  const [view, setView] = useStateTS("list");
  const [openTopicId, setOpenTopicId] = useStateTS(null);

  const openTopic = (id) => { setOpenTopicId(id); setView("detail"); };
  const openNew = () => { setView("new"); };
  const backToList = () => { setView("list"); setOpenTopicId(null); };

  if (view === "detail" && openTopicId) {
    return <TownSquareDetail user={user} topicId={openTopicId} onBack={backToList} />;
  }
  if (view === "new") {
    return <TownSquareNewTopic user={user} onCancel={backToList}
      onCreated={(topic) => openTopic(topic.id)} />;
  }
  return <TownSquareList user={user} onOpen={openTopic} onNew={openNew} />;
}


function TownSquareList({ user, onOpen, onNew }) {
  const [cats, setCats] = useStateTS([]);
  const [activeSlug, setActiveSlug] = useStateTS("");
  const [hideResolved, setHideResolved] = useStateTS(true);
  // searchInput = what the user is typing right now (re-renders every
  // keystroke for the input value). searchQuery = the debounced value
  // we actually send to the API. Keeps the network quiet while typing.
  const [searchInput, setSearchInput] = useStateTS("");
  const [searchQuery, setSearchQuery] = useStateTS("");
  const [state, setState] = useStateTS({ loading: true, error: null, topics: [], page: 1, hasMore: false });
  const reqRef = useRefTS(0);

  // Categories load once on mount. The /categories endpoint returns
  // hidden ones too for managers (so the dashboard editor can offer
  // "Show"), but the tab strip is consumed by everyone — filter to
  // active-only here. Otherwise a manager who hides a category would
  // still see it in their own tab strip / new-topic dropdown and
  // submitting against it would 400 (codex P2 on PR #201).
  useEffectTS(() => {
    let cancelled = false;
    window.Api.townSquareCategories()
      .then((data) => {
        if (cancelled) return;
        const active = (data || []).filter((c) => c.is_active !== false);
        setCats(active);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Debounce: only push the search to the API 250ms after typing
  // stops. Backend ignores queries shorter than 2 chars anyway, but
  // we cap that here too so a single keystroke doesn't even fire.
  useEffectTS(() => {
    const trimmed = searchInput.trim();
    const t = setTimeout(() => {
      setSearchQuery(trimmed.length >= 2 ? trimmed : "");
    }, 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const reload = (page = 1) => {
    const my = ++reqRef.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    window.Api.townSquareTopics({
      category: activeSlug || undefined,
      page,
      filter: hideResolved ? "active" : undefined,
      q: searchQuery || undefined,
    })
      .then((data) => {
        if (my !== reqRef.current) return;
        setState({
          loading: false, error: null,
          topics: data.topics || [],
          page: data.page || 1,
          hasMore: !!data.has_more,
        });
      })
      .catch((err) => {
        if (my !== reqRef.current) return;
        setState({ loading: false, error: err.message || "Couldn't load Town Square.", topics: [], page: 1, hasMore: false });
      });
  };

  // Reload on filter / category / search change. Always resets to page
  // 1 — staying on page 5 after typing a query would feel broken.
  useEffectTS(() => { reload(1); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeSlug, hideResolved, searchQuery]);

  const { loading, error, topics, page, hasMore } = state;
  const isSearching = searchQuery.length > 0;

  return (
    <div className="scroll">
      <div className="stack-md" style={{ maxWidth: 880 }}>
        <Banner icon="users">
          <b>Town Square.</b> Talk to your neighbors — rule change ideas, committee forming, suggestions, anything. Every active member can post and reply.
        </Banner>

        <div className="ts-tabs" role="tablist" aria-label="Categories">
          <button
            role="tab"
            aria-selected={activeSlug === ""}
            className={`ts-tab ${activeSlug === "" ? "ts-tab--on" : ""}`}
            onClick={() => setActiveSlug("")}
          >
            All
          </button>
          {cats.map((c) => (
            <button
              key={c.slug}
              role="tab"
              aria-selected={activeSlug === c.slug}
              className={`ts-tab ${activeSlug === c.slug ? "ts-tab--on" : ""}`}
              onClick={() => setActiveSlug(c.slug)}
            >
              {c.name}
            </button>
          ))}
        </div>

        <div className="ts-search">
          <Icon name="search" size={16} />
          <input
            type="search"
            className="ts-search__input"
            placeholder="Search topics (title or body) — try a phrase like 'original landscape'"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            maxLength={200}
            aria-label="Search topics"
          />
          {searchInput && (
            <button
              type="button"
              className="ts-search__clear"
              onClick={() => setSearchInput("")}
              aria-label="Clear search"
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>

        <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <label className="row" style={{ gap: 8, fontSize: 14, color: "var(--mute)" }}>
            <input
              type="checkbox"
              checked={hideResolved}
              onChange={(e) => setHideResolved(e.target.checked)}
            />
            Hide resolved
          </label>
          <button className="btn btn--primary" onClick={onNew}>
            <Icon name="spark" size={14} /> New topic
          </button>
        </div>

        {loading ? (
          <div className="empty"><b>Loading…</b></div>
        ) : error ? (
          <div className="empty" style={{ color: "var(--danger, #c0392b)" }}>{error}</div>
        ) : topics.length === 0 ? (
          <div className="empty">
            <Icon name={isSearching ? "search" : "users"} size={28} style={{ opacity: .35 }} />
            {isSearching ? (
              <p style={{ marginTop: 10 }}>
                <b>No topics match &ldquo;{searchQuery}&rdquo;.</b> Try a different word or clear the search.
              </p>
            ) : (
              <>
                <p style={{ marginTop: 10 }}>
                  <b>No topics yet.</b> Start the first one — your neighbors are waiting.
                </p>
                <button className="btn btn--primary" style={{ marginTop: 12 }} onClick={onNew}>
                  <Icon name="spark" size={14} /> New topic
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="ts-list">
            {topics.map((t) => (
              <TownSquareCard key={t.id} topic={t} onOpen={() => onOpen(t.id)} />
            ))}
          </div>
        )}

        {(page > 1 || hasMore) && (
          <div className="row" style={{ justifyContent: "center", gap: 8, marginTop: 12 }}>
            <button className="btn btn--ghost" disabled={page <= 1} onClick={() => reload(page - 1)}>
              ‹ Prev
            </button>
            <span className="text-sm text-mute">Page {page}</span>
            <button className="btn btn--ghost" disabled={!hasMore} onClick={() => reload(page + 1)}>
              Next ›
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


function TownSquareCard({ topic, onOpen }) {
  return (
    <button type="button" className="ts-card" onClick={onOpen}>
      <div className="ts-card__flags">
        {topic.is_pinned && <span className="ts-flag ts-flag--pin" title="Pinned">📌 Pinned</span>}
        {topic.is_locked && <span className="ts-flag ts-flag--lock" title="Locked">🔒 Locked</span>}
        {topic.is_resolved && <span className="ts-flag ts-flag--done" title="Resolved">✓ Resolved</span>}
        {topic.category && <span className="ts-flag ts-flag--cat">{topic.category.name}</span>}
      </div>
      <div className="ts-card__title">{topic.title}</div>
      {topic.snippet && <div className="ts-card__snippet">{topic.snippet}</div>}
      <div className="ts-card__meta">
        <span>{topic.author?.display_name || "Member"}</span>
        <span>·</span>
        <span title={new Date(topic.last_activity_at).toLocaleString()}>{_relTimeTS(topic.last_activity_at)}</span>
        <span style={{ marginLeft: "auto" }} className="ts-card__counts">
          <span title="Replies">💬 {topic.reply_count}</span>
          <span title="Agreed">👍 {topic.reaction_count}</span>
        </span>
      </div>
    </button>
  );
}


function TownSquareDetail({ user, topicId, onBack }) {
  const [state, setState] = useStateTS({ loading: true, error: null, topic: null, replies: [] });
  const [draft, setDraft] = useStateTS("");
  const [posting, setPosting] = useStateTS(false);
  const [postError, setPostError] = useStateTS(null);
  const canModerate = _canModerate(user);

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    window.Api.townSquareGetTopic(topicId)
      .then((data) => setState({ loading: false, error: null, topic: data.topic, replies: data.replies || [] }))
      .catch((err) => setState({ loading: false, error: err.message || "Couldn't load this topic.", topic: null, replies: [] }));
  };

  useEffectTS(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [topicId]);

  const reactTopic = () => {
    if (!state.topic) return;
    window.Api.townSquareReactTopic(state.topic.id)
      .then(({ reacted, reaction_count }) => {
        setState((s) => ({ ...s, topic: { ...s.topic, user_reacted: reacted, reaction_count } }));
      })
      .catch(() => {});
  };

  const reactReply = (replyId) => {
    window.Api.townSquareReactReply(replyId)
      .then(({ reacted, reaction_count }) => {
        setState((s) => ({
          ...s,
          replies: s.replies.map((r) => r.id === replyId ? { ...r, user_reacted: reacted, reaction_count } : r),
        }));
      })
      .catch(() => {});
  };

  const postReply = () => {
    const body = draft.trim();
    if (!body || posting) return;
    setPosting(true); setPostError(null);
    window.Api.townSquareReply(topicId, body)
      .then((reply) => {
        setDraft("");
        setState((s) => ({
          ...s,
          topic: s.topic && { ...s.topic, reply_count: (s.topic.reply_count || 0) + 1 },
          replies: [...s.replies, reply],
        }));
      })
      .catch((err) => setPostError(err.message || "Couldn't post reply."))
      .finally(() => setPosting(false));
  };

  const moderate = (patch) => {
    window.Api.townSquareModerate(topicId, patch)
      .then((topic) => setState((s) => ({ ...s, topic })))
      .catch(() => {});
  };

  if (state.loading) return <div className="scroll"><div className="empty"><b>Loading…</b></div></div>;
  if (state.error) {
    return (
      <div className="scroll">
        <button className="btn btn--ghost" onClick={onBack}><Icon name="chevron" size={14} style={{ transform: "rotate(180deg)" }} /> Back</button>
        <div className="empty" style={{ color: "var(--danger, #c0392b)" }}>{state.error}</div>
      </div>
    );
  }
  const t = state.topic;
  if (!t) return null;

  return (
    <div className="scroll">
      <div className="stack-md" style={{ maxWidth: 760 }}>
        <button className="btn btn--ghost" onClick={onBack} style={{ alignSelf: "flex-start" }}>
          <Icon name="chevron" size={14} style={{ transform: "rotate(180deg)" }} /> Back to Town Square
        </button>

        <div className="ts-detail__flags">
          {t.is_pinned && <span className="ts-flag ts-flag--pin">📌 Pinned</span>}
          {t.is_locked && <span className="ts-flag ts-flag--lock">🔒 Locked</span>}
          {t.is_resolved && <span className="ts-flag ts-flag--done">✓ Resolved</span>}
          {t.category && <span className="ts-flag ts-flag--cat">{t.category.name}</span>}
        </div>

        <h1 className="ts-detail__title">{t.title}</h1>
        <div className="ts-detail__meta">
          <span>{t.author?.display_name || "Member"}</span>
          <span>·</span>
          <span title={new Date(t.created_at).toLocaleString()}>{_relTimeTS(t.created_at)}</span>
        </div>

        <div className="ts-detail__body">
          {t.body.split("\n").map((line, i) => <p key={i}>{line || " "}</p>)}
        </div>

        <div className="row" style={{ gap: 10, alignItems: "center" }}>
          <button
            type="button"
            className={`ts-react ${t.user_reacted ? "ts-react--on" : ""}`}
            onClick={reactTopic}
            aria-pressed={!!t.user_reacted}
          >
            👍 Agree · {t.reaction_count}
          </button>
          {canModerate && (
            <div className="row" style={{ gap: 6, marginLeft: "auto" }}>
              <button className="btn btn--ghost btn--sm" onClick={() => moderate({ is_pinned: !t.is_pinned })}>
                {t.is_pinned ? "Unpin" : "Pin"}
              </button>
              <button className="btn btn--ghost btn--sm" onClick={() => moderate({ is_locked: !t.is_locked })}>
                {t.is_locked ? "Unlock" : "Lock"}
              </button>
              <button className="btn btn--ghost btn--sm" onClick={() => moderate({ is_resolved: !t.is_resolved })}>
                {t.is_resolved ? "Reopen" : "Mark resolved"}
              </button>
            </div>
          )}
        </div>

        <div className="ts-replies">
          <div className="ts-replies__hd">
            {state.replies.length} {state.replies.length === 1 ? "reply" : "replies"}
          </div>
          {state.replies.map((r) => (
            <div key={r.id} className="ts-reply">
              <div className="ts-reply__meta">
                <span>{r.author?.display_name || "Member"}</span>
                <span>·</span>
                <span title={new Date(r.created_at).toLocaleString()}>{_relTimeTS(r.created_at)}</span>
              </div>
              <div className="ts-reply__body">
                {r.body.split("\n").map((line, i) => <p key={i}>{line || " "}</p>)}
              </div>
              <button
                type="button"
                className={`ts-react ts-react--sm ${r.user_reacted ? "ts-react--on" : ""}`}
                onClick={() => reactReply(r.id)}
                aria-pressed={!!r.user_reacted}
              >
                👍 {r.reaction_count}
              </button>
            </div>
          ))}
        </div>

        {t.is_locked ? (
          <div className="empty" style={{ padding: "16px 12px" }}>
            <Icon name="settings" size={20} style={{ opacity: .5 }} />
            <p style={{ marginTop: 6 }}>This topic is locked. No new replies.</p>
          </div>
        ) : (
          <div className="ts-compose">
            <label htmlFor="ts-reply-input" className="ts-compose__label">Add a reply</label>
            <textarea
              id="ts-reply-input"
              className="textarea"
              rows={3}
              placeholder="Share your thoughts…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={posting}
            />
            {postError && <div className="text-sm" style={{ color: "var(--danger, #c0392b)" }}>{postError}</div>}
            <div className="row" style={{ justifyContent: "flex-end", marginTop: 8 }}>
              <button className="btn btn--primary" disabled={!draft.trim() || posting} onClick={postReply}>
                {posting ? "Posting…" : "Post reply"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function TownSquareNewTopic({ user, onCancel, onCreated }) {
  const [cats, setCats] = useStateTS([]);
  const [title, setTitle] = useStateTS("");
  const [body, setBody] = useStateTS("");
  const [category, setCategory] = useStateTS("");
  const [submitting, setSubmitting] = useStateTS(false);
  const [error, setError] = useStateTS(null);

  useEffectTS(() => {
    let cancelled = false;
    window.Api.townSquareCategories()
      .then((data) => {
        if (cancelled) return;
        // Active-only — hidden categories would be selectable here but
        // create_topic 400s on inactive categories, so we'd give the
        // user a confusing failure path (codex P2 on PR #201).
        const active = (data || []).filter((c) => c.is_active !== false);
        setCats(active);
        if (active.length && !category) setCategory(active[0].slug);
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = () => {
    if (submitting) return;
    setSubmitting(true); setError(null);
    window.Api.townSquareCreateTopic({ title: title.trim(), body: body.trim(), category })
      .then((topic) => { if (onCreated) onCreated(topic); })
      .catch((err) => setError(err.message || "Couldn't post topic."))
      .finally(() => setSubmitting(false));
  };

  const canSubmit = title.trim().length > 0 && body.trim().length > 0 && category && !submitting;

  return (
    <div className="scroll">
      <div className="stack-md" style={{ maxWidth: 760 }}>
        <button className="btn btn--ghost" onClick={onCancel} style={{ alignSelf: "flex-start" }}>
          <Icon name="chevron" size={14} style={{ transform: "rotate(180deg)" }} /> Cancel
        </button>
        <h1 className="ts-detail__title">New topic</h1>
        <div className="ts-compose">
          <label htmlFor="ts-new-cat" className="ts-compose__label">Category</label>
          <select
            id="ts-new-cat"
            className="select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={submitting || cats.length === 0}
          >
            {cats.length === 0 && <option>Loading…</option>}
            {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>

          <label htmlFor="ts-new-title" className="ts-compose__label" style={{ marginTop: 14 }}>Title</label>
          <input
            id="ts-new-title"
            className="input"
            placeholder="A short, clear headline"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            disabled={submitting}
          />

          <label htmlFor="ts-new-body" className="ts-compose__label" style={{ marginTop: 14 }}>Body</label>
          <textarea
            id="ts-new-body"
            className="textarea"
            rows={8}
            placeholder="Lay out what you're proposing, asking, or discussing. Be respectful — your neighbors will read this."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={submitting}
          />

          {error && <div className="text-sm" style={{ color: "var(--danger, #c0392b)", marginTop: 8 }}>{error}</div>}
          <div className="row" style={{ justifyContent: "flex-end", marginTop: 12, gap: 8 }}>
            <button className="btn btn--ghost" onClick={onCancel} disabled={submitting}>Cancel</button>
            <button className="btn btn--primary" disabled={!canSubmit} onClick={submit}>
              {submitting ? "Posting…" : "Post topic"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


export { TownSquare };
